import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "../ui/button";
import { Briefcase, ClipboardText, PencilSimple, ArrowSquareOut, MapPinLine, Path, X, CheckSquare, PaperPlaneTilt } from "@phosphor-icons/react";
import { propertyAddress, raiseLinks } from "./PropertyCard";
import RouteDayDialog from "./RouteDayDialog";

export const PIN_COLOURS = ["#7c3aed", "#0891b2", "#d97706", "#059669", "#db2777", "#2563eb", "#ea580c", "#4f46e5", "#65a30d", "#b91c1c", "#0d9488", "#c026d3", "#ca8a04", "#475569", "#e11d48", "#0284c7"];

/** Pin colour per client and sub-client — the client's stored colour (same dot everywhere in the app), falling back to palette order. */
export const clientColours = (tree) => {
  const m = {};
  let i = 0;
  tree.forEach((c) => { m[c.id] = c.colour || PIN_COLOURS[i++ % PIN_COLOURS.length]; (c.sub_clients || []).forEach((s) => { m[s.id] = s.colour || PIN_COLOURS[i++ % PIN_COLOURS.length]; }); });
  return m;
};

const pinIcon = (colour, label, { picked = false, dim = false } = {}) => L.divIcon({
  className: "",
  html: `<div style="width:${picked ? 32 : 28}px;height:${picked ? 32 : 28}px;background:${picked ? "#0f172a" : colour};border:${picked ? 3 : 2}px solid ${picked ? colour : "#fff"};border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 2px 6px rgba(0,0,0,.35);opacity:${dim ? 0.45 : 1};display:flex;align-items:center;justify-content:center;transition:opacity .15s"><span style="transform:rotate(45deg);color:#fff;font:700 11px/1 Chivo,sans-serif">${label}</span></div>`,
  iconSize: [32, 32], iconAnchor: [16, 32], popupAnchor: [0, -30],
});

function FitBounds({ points }) {
  const map = useMap();
  const key = points.map((p) => p.id).join("|");
  useEffect(() => {
    if (!points.length) return;
    map.fitBounds(L.latLngBounds(points.map((p) => [p.latitude, p.longitude])), { padding: [48, 48], maxZoom: 15 });
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

/** Portfolio map: one pin per placed property, coloured by main client. `tree` = /properties clients tree.
 *  "Plan a day" mode turns pins into a multi-select that books a route of visits for an operative. */
export default function PropertiesMap({ properties, tree, onEdit, onPickClient, unplaced, locating, onLocate }) {
  const navigate = useNavigate();
  const [planning, setPlanning] = useState(false);
  const [picked, setPicked] = useState([]); // property ids in pick order
  const [routeDialog, setRouteDialog] = useState(false);
  // Every client and sub-client gets its own pin colour (assigned in tree order so neighbours differ)
  const colourOf = useMemo(() => clientColours(tree), [tree]);
  const placed = properties.filter((p) => p.latitude != null && p.longitude != null);
  const countFor = (id) => placed.filter((p) => p.client_id === id).length;
  const legend = tree
    .map((c) => ({ ...c, count: countFor(c.id), sub_clients: c.sub_clients.map((s) => ({ ...s, count: countFor(s.id) })).filter((s) => s.count > 0) }))
    .filter((c) => c.count > 0 || c.sub_clients.length > 0);
  const pickedProps = picked.map((id) => properties.find((p) => p.id === id)).filter(Boolean);
  const toggle = (id) => setPicked((l) => (l.includes(id) ? l.filter((x) => x !== id) : [...l, id]));
  const exitPlanning = () => { setPlanning(false); setPicked([]); };
  const chip = (c, sub = false) => (
    <button key={c.id} type="button" onClick={() => onPickClient(c.id)} title={sub ? `Sub-client — show only ${c.name}` : `Show ${c.name}${c.sub_clients?.length ? " and its sub-clients" : ""}`}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border transition-colors ${sub ? "border-transparent hover:bg-muted text-muted-foreground hover:text-foreground" : "border-border hover:bg-muted font-medium"}`}
      data-testid={`properties-map-legend-${c.id}`}>
      <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: colourOf[c.id] }} /> {c.name} <span className="text-muted-foreground font-normal">{c.count}</span>
    </button>
  );

  return (
    <div className="space-y-3" data-testid="properties-map">
      <div className="flex flex-wrap items-center gap-2" data-testid="properties-map-legend">
        {legend.map((c) => c.sub_clients.length === 0 ? chip(c) : (
          <div key={c.id} className="inline-flex flex-wrap items-center gap-0.5 rounded-full border border-border/70 bg-muted/20 p-0.5 pr-1" data-testid={`properties-map-legend-group-${c.id}`}>
            {chip(c)}
            <span className="text-muted-foreground/60 text-[10px] px-0.5">›</span>
            {c.sub_clients.map((s) => chip(s, true))}
          </div>
        ))}
        <span className="ml-auto text-[11px] text-muted-foreground" data-testid="properties-map-placed">{placed.length} of {properties.length} pinned</span>
        {planning ? (
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={exitPlanning} data-testid="properties-map-plan-cancel"><X size={12} className="mr-1" /> Exit planning</Button>
        ) : (
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setPlanning(true)} disabled={placed.length === 0} title="Pick pins and book them as a day of visits for an operative" data-testid="properties-map-plan-btn"><Path size={13} className="mr-1" /> Plan a day</Button>
        )}
      </div>
      {planning && (
        <div className="border border-primary/40 bg-primary/5 rounded-md px-3 py-2 flex flex-wrap items-center gap-2" data-testid="properties-map-plan-bar">
          <p className="text-xs font-medium" data-testid="properties-map-plan-count">{picked.length ? `${picked.length} pin${picked.length === 1 ? "" : "s"} picked` : "Click pins to pick the properties to visit"}</p>
          <div className="flex flex-wrap gap-1 min-w-0">
            {pickedProps.map((p, i) => (
              <button key={p.id} type="button" onClick={() => toggle(p.id)} className="inline-flex items-center gap-1 text-[11px] bg-card border border-border rounded-full pl-1.5 pr-2 py-0.5 hover:border-red-300" title="Remove from the day" data-testid={`properties-map-plan-chip-${p.id}`}>
                <span className="h-4 w-4 rounded-full bg-slate-900 text-white text-[9px] font-bold flex items-center justify-center">{i + 1}</span> {p.name} <X size={10} className="text-muted-foreground" />
              </button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-1.5">
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setPicked(placed.map((p) => p.id))} disabled={picked.length === placed.length} data-testid="properties-map-plan-select-all"><CheckSquare size={12} className="mr-1" /> Pick all shown</Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setPicked([])} disabled={!picked.length} data-testid="properties-map-plan-clear">Clear</Button>
            <Button size="sm" className="h-7 text-xs" onClick={() => setRouteDialog(true)} disabled={!picked.length} data-testid="properties-map-plan-send"><PaperPlaneTilt size={12} className="mr-1" /> Send to route planner</Button>
          </div>
        </div>
      )}
      <div className="border border-border rounded-lg overflow-hidden h-[560px] relative z-0">
        {placed.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center p-6" data-testid="properties-map-empty">
            <MapPinLine size={40} className="text-muted-foreground/40 mb-3" />
            <p className="text-sm text-muted-foreground">{locating ? "Locating properties…" : properties.length ? "None of these properties has a map location yet." : "No properties to show."}</p>
          </div>
        ) : (
          <MapContainer center={[placed[0].latitude, placed[0].longitude]} zoom={12} scrollWheelZoom className="h-full w-full">
            <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <FitBounds points={placed} />
            {placed.map((p) => {
              const idx = picked.indexOf(p.id);
              const letter = (p.parent_client_name || p.client_name || "?")[0].toUpperCase();
              const icon = pinIcon(colourOf[p.client_id] || "#475569", idx >= 0 ? String(idx + 1) : letter, { picked: idx >= 0, dim: planning && idx < 0 && picked.length > 0 });
              return planning ? (
                <Marker key={`${p.id}-plan`} position={[p.latitude, p.longitude]} icon={icon} eventHandlers={{ click: () => toggle(p.id) }} title={`${p.name} — click to ${idx >= 0 ? "remove from" : "add to"} the day`} />
              ) : (
                <Marker key={p.id} position={[p.latitude, p.longitude]} icon={icon}>
                  <Popup minWidth={240}>
                    <div className="space-y-1.5" data-testid={`properties-map-popup-${p.id}`}>
                      <p className="font-semibold text-sm leading-tight">{p.name}</p>
                      <p className="text-xs text-slate-600">{propertyAddress(p)}</p>
                      <p className="text-[11px] text-slate-500">{p.parent_client_name ? `${p.parent_client_name} › ` : ""}{p.client_name}</p>
                      <div className="flex flex-wrap gap-1 pt-1">
                        <Button size="sm" className="h-6 text-[11px] px-2" onClick={() => navigate(raiseLinks(p).job)} data-testid={`properties-map-new-job-${p.id}`}><Briefcase size={11} className="mr-1" /> New job</Button>
                        <Button size="sm" variant="outline" className="h-6 text-[11px] px-2" onClick={() => navigate(raiseLinks(p).request)} data-testid={`properties-map-new-request-${p.id}`}><ClipboardText size={11} className="mr-1" /> New request</Button>
                        <Button size="sm" variant="ghost" className="h-6 text-[11px] px-2" onClick={() => onEdit(p)} data-testid={`properties-map-edit-${p.id}`}><PencilSimple size={11} className="mr-1" /> Edit</Button>
                        <Link to={`/clients/${p.client_id}`} className="inline-flex items-center h-6 px-2 text-[11px] text-primary hover:underline" data-testid={`properties-map-open-client-${p.id}`}><ArrowSquareOut size={11} className="mr-1" /> Client</Link>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        )}
      </div>
      {(unplaced.length > 0 || locating) && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground border border-dashed border-border rounded-md px-3 py-2" data-testid="properties-map-unplaced">
          <MapPinLine size={14} className="text-amber-600 shrink-0" />
          {locating ? <span>Locating {unplaced.length} propert{unplaced.length === 1 ? "y" : "ies"} on the map…</span> : (
            <>
              <span>{unplaced.length} propert{unplaced.length === 1 ? "y" : "ies"} not on the map yet: <span className="text-foreground">{unplaced.slice(0, 6).map((p) => p.name).join(", ")}{unplaced.length > 6 ? ` +${unplaced.length - 6} more` : ""}</span>. Check the address and postcode, or try again.</span>
              <Button size="sm" variant="outline" className="h-6 text-[11px] px-2 ml-auto" onClick={onLocate} data-testid="properties-map-locate-btn">Try again</Button>
            </>
          )}
        </div>
      )}
      <RouteDayDialog open={routeDialog} onClose={() => setRouteDialog(false)} properties={pickedProps} onBooked={() => { setPlanning(false); setPicked([]); }} />
    </div>
  );
}
